import { makeVar, useReactiveVar } from '@apollo/client'

const CHOICE_KEY_PREFIX = 'xyro:wallet-choice:'
const APPS_KEY = 'xyro:wallet-apps'

interface WalletChoice {
  userId: string
  walletName: string | null
}

/**
 * The latest choice made in this tab. Storage keeps it across reloads; this
 * var re-renders the readers and stands in when storage refuses to write.
 */
const latestChoiceVar = makeVar<WalletChoice | null>(null)

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // NOTE: storage refused the write; the value lives until the page reloads
  }
}

/**
 * The wallet app (wallet-adapter name) the user last connected on purpose on
 * this device, for this account; null when they never picked one. The
 * Privy auto-selection and wallet-adapter's autoConnect respect it.
 */
export const readWalletChoice = (userId: string): string | null => {
  const latest = latestChoiceVar()
  if (latest?.userId === userId) return latest.walletName

  return readStorage(`${CHOICE_KEY_PREFIX}${userId}`)
}

/** Remembers `walletName` as the user's choice; null forgets it. */
export const rememberWalletChoice = (
  userId: string,
  walletName: string | null
) => {
  writeStorage(`${CHOICE_KEY_PREFIX}${userId}`, walletName)
  latestChoiceVar({ userId, walletName })
}

/** The user's wallet choice; re-renders when it changes in this tab. */
export const useWalletChoice = (userId: string | undefined) => {
  useReactiveVar(latestChoiceVar)

  return userId ? readWalletChoice(userId) : null
}

/**
 * Runs `connect` with `walletName` as the user's choice, so nothing switches
 * the wallet back while it connects; a failed attempt restores the previous
 * choice.
 */
export const withWalletChoice = async <T>(
  userId: string,
  walletName: string,
  connect: () => Promise<T>
): Promise<T> => {
  const previous = readWalletChoice(userId)
  rememberWalletChoice(userId, walletName)

  try {
    return await connect()
  } catch (error) {
    rememberWalletChoice(userId, previous)
    throw error
  }
}

const readWalletApps = (): Record<string, string> => {
  try {
    const parsed: unknown = JSON.parse(readStorage(APPS_KEY) ?? '{}')
    return parsed && typeof parsed === 'object' ?
        (parsed as Record<string, string>)
      : {}
  } catch {
    return {}
  }
}

/** The wallet app that last connected `address` on this device. */
export const readWalletApp = (address: string): string | null =>
  readWalletApps()[address] ?? null

export const rememberWalletApp = (address: string, walletName: string) => {
  const apps = readWalletApps()
  if (apps[address] === walletName) return

  writeStorage(APPS_KEY, JSON.stringify({ ...apps, [address]: walletName }))
}
